<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps<{
  message: string;
  type?: 'error' | 'success' | 'warning' | 'info';
}>();

const visible = ref(false);

watch(() => props.message, (val) => {
  if (val) visible.value = true;
}, { immediate: true });

const close = () => { visible.value = false; };

const titles: Record<string, string> = {
  error: 'Something went wrong',
  success: 'Success',
  warning: 'Heads up',
  info: 'Note',
};
</script>

<template>
  <Transition name="alert-drop">
    <div v-if="visible && message" class="aa-wrap" :class="`aa-wrap--${type ?? 'error'}`" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="aa-icon-col">
        <!-- Error -->
        <svg v-if="!type || type === 'error'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <!-- Success -->
        <svg v-else-if="type === 'success'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        <!-- Warning -->
        <svg v-else-if="type === 'warning'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <!-- Info -->
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      </div>

      <div class="aa-body">
        <div class="aa-title">{{ titles[type ?? 'error'] }}</div>
        <div class="aa-message">{{ message }}</div>
      </div>

      <button class="aa-close" @click="close" aria-label="Dismiss">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.aa-wrap {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-family: 'DM Sans', sans-serif;
  position: relative;
  border: 1px solid transparent;
}

.aa-wrap--error   { background: #fff1f2; border-color: #fecdd3; color: #9f1239; }
.aa-wrap--success { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
.aa-wrap--warning { background: #fffbeb; border-color: #fde68a; color: #92400e; }
.aa-wrap--info    { background: #eff6ff; border-color: #bfdbfe; color: #1e40af; }

.aa-icon-col {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.aa-wrap--error   .aa-icon-col { background: #ffe4e6; }
.aa-wrap--success .aa-icon-col { background: #dcfce7; }
.aa-wrap--warning .aa-icon-col { background: #fef3c7; }
.aa-wrap--info    .aa-icon-col { background: #dbeafe; }

.aa-body { flex: 1; min-width: 0; }

.aa-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin-bottom: 2px;
}

.aa-message {
  font-size: 0.82rem;
  font-weight: 400;
  line-height: 1.5;
  opacity: 0.9;
}

.aa-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: currentColor;
  opacity: 0.45;
  padding: 2px;
  display: flex;
  align-items: center;
  transition: opacity 0.15s;
  margin-top: 1px;
}

.aa-close:hover { opacity: 0.85; }

.alert-drop-enter-active { transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
.alert-drop-leave-active { transition: all 0.15s ease; }
.alert-drop-enter-from   { opacity: 0; transform: translateY(-8px) scale(0.98); }
.alert-drop-leave-to     { opacity: 0; transform: translateY(-4px) scale(0.99); }
</style>
